
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Column, { FilterOption } from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import moment from 'moment/moment'
import axios from 'axios'
import ColumnOptions from '@/components/listview/ColumnOptions.vue'
import IssueBulkUpdater from '@/components/listview/IssueBulkUpdater.vue'
import { ServiceStates } from '@/types/serviceItem'
import SubscribeButton from '@/components/base/SubscribeButton.vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import ListviewSettings from '@/types/listviewSettings'
import ls from '@/ls'
import { can } from '@/globalFunctions'
import { WrappedResponse } from 'vue-api-query'
import IssueModel from '@/models/IssueModel'
import { realtimeService, RealtimeUnsubscribe } from '@/services/realtime'

@Component({
  methods: { can },
  components: { PageHeader, SearchableColumnFilter, SubscribeButton, IssueBulkUpdater, ColumnOptions, ActiveFilters }
})

export default class Issues extends Vue {
  @Prop({ default: false }) rental!:boolean

  moment = moment
  realtimeSubscriptions: RealtimeUnsubscribe[] = []

  config = {
    entity: 'issue',
    entities: 'issues',
    single: 'Melding',
    plural: 'Meldingen',
    subtitle: 'Overzicht van meldingen.'
  }

  settings: ListviewSettings = {
    routeNames: { create: 'issueCreate', show: 'issueShow', edit: 'issueEdit' },
    routeParamKey: 'issueId',
    permissionNames: { show: 'issues.view', edit: 'issues.edit', delete: 'issues.delete' },
    functionNames: { getList: 'getIssueList' },
    getterNames: { list: 'issueList', loading: 'issueLoading' },
    localStorageName: 'issueTableSettings'
  }

  searchValue = ''
  filterVisible = false
  newIssueCount = 0
  sortOrder = { column: 'created_at', order: 'descend' }
  conditions = {
    status: [ServiceStates.Gesloten]
  }

  localSettings = ls.get(this.settings.localStorageName)
  tableSettings:TableSettings = TableSettings.from(this.localSettings, this.conditions, this.sortOrder)
  currentTab:string = ls.get('issuesListActiveTab') || 'details'

  columns : Column[] = [
    {
      title: '!',
      filterLabel: 'actief',
      key: 'read',
      dataIndex: 'read',
      scopedSlots: { customRender: 'read', filterDropdown: 'radioFilter' },
      filters: [
        { text: 'Gelezen', value: 'true' },
        { text: 'Ongelezen', value: 'false' }
      ]
    },
    {
      title: '#',
      key: 'id',
      dataIndex: 'id',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      scopedSlots: { customRender: 'type' },
      filters: [],
      sorter: false
    },
    {
      title: 'Bedrijf',
      key: 'project.entity.id',
      dataIndex: 'project.entity',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend'],
      scopedSlots: { customRender: 'entity' }
    },
    {
      title: 'I.B.',
      key: 'manager_id',
      dataIndex: 'manager',
      scopedSlots: { customRender: 'manager', filterDropdown: 'customFilter' },
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend'],
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      width: 90
    },
    {
      title: 'Onderwerp',
      key: 'title',
      dataIndex: 'title',
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend'],
      scopedSlots: { customRender: 'project', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible)
    },
    {
      title: 'Adres',
      key: 'address',
      dataIndex: 'building',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend'],
      scopedSlots: { customRender: 'address' }
    },
    {
      title: 'Bwnr',
      key: 'number',
      dataIndex: 'building.number',
      filters: [],
      scopedSlots: { filterDropdown: 'customFilter', customRender: 'number' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Oplossend bedrijf',
      key: 'solver_company.id',
      dataIndex: 'solver_company',
      scopedSlots: { filterDropdown: 'customFilter', customRender: 'solver_company' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Oplosser',
      key: 'solver.id',
      dataIndex: 'solver',
      scopedSlots: { filterDropdown: 'customFilter', customRender: 'solver' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bron',
      key: 'source',
      dataIndex: 'source',
      scopedSlots: { filterDropdown: 'customFilter', customRender: 'source' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Prio',
      key: 'priority',
      dataIndex: 'priority',
      scopedSlots: { customRender: 'priority' },
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Gemeld',
      key: 'created_at',
      dataIndex: 'created_at',
      scopedSlots: { customRender: 'created_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      width: 110
    },
    {
      title: 'Deadline',
      key: 'estimate',
      dataIndex: 'estimate',
      scopedSlots: { customRender: 'estimate' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      width: 110
    },
    {
      title: 'Afspraak',
      key: 'appointment_at',
      dataIndex: 'appointment_at',
      scopedSlots: { customRender: 'appointment_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      width: 110
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      scopedSlots: { customRender: 'status' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      width: 140
    },
    {
      title: 'Actie\'s',
      key: 'action',
      filters: [],
      scopedSlots: { customRender: 'action' }
    }
  ]

  timer?:number
  timeout = 1000

  selectedRowKeys:number[] = []

  mounted () : void {
    this.searchValue = this.tableSettings.search
    this.init()
  }

  beforeDestroy () : void {
    this.cancelFetchingIssues()
    this.realtimeSubscriptions.forEach(unsubscribe => unsubscribe())
    this.realtimeSubscriptions = []
    this.$message.destroy()
  }

  async init (): Promise<void> {
    this.reload()
    this.onTableSettingsChange()

    this.realtimeSubscriptions = [
      realtimeService.listenPrivate('issues', '.issue.changed', () => {
        this.reload()
      }),
      realtimeService.listenPrivate('issues', '.issue.created', () => {
        this.ShowNewIssueMessage()
      })
    ]
  }

  ShowNewIssueMessage () : void {
    this.newIssueCount += 1
    this.$message.info({
      content: h => h('span', {
        style: 'cursor: pointer;',
        on: { click: () => { this.$message.destroy(); this.reload(); this.newIssueCount = 0 } }
      }, [
        `[${new Date().toLocaleTimeString()}] Er ${this.newIssueCount > 1 ? 'zijn' : 'is'} `,
        h('b', `${this.newIssueCount}`),
        ` nieuwe melding${this.newIssueCount > 1 ? 'en' : ''}. Klik hier om te verversen.`
      ]),
      duration: 0,
      key: 'newIssueMessage'
    })
  }

  reload () : void {
    this.$store.dispatch('issueStore/getList', { settings: this.tableSettings })
    this.newIssueCount = 0
    this.$message.destroy()
  }

  filterDropdownVisibleChange (visible:boolean) : void {
    this.filterVisible = visible
  }

  get activeFilters () : Record<string, (string|number|boolean)[]> {
    return this.tableSettings.activeFilters
  }

  toggleInactiveItems (event: {target: { checked: boolean } }) : void {
    this.tableSettings.archived = event.target.checked
    this.reload()
  }

  toggleUnrelatedItems (event: {target: { checked: boolean } }) : void {
    this.tableSettings.unrelated = event.target.checked
    this.reload()
  }

  // ColumnOptions functions START
  get activeColumns (): Column[] {
    return this.columns.filter(col => !this.$store.getters['issueStore/inactiveColumns'].includes(col.key))
  }

  // Required for ActiveFilters component
  updateFilters (filters: TableSettings['activeFilters'], archived:boolean, unrelated:boolean) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = filters
    this.tableSettings.archived = archived
    this.tableSettings.unrelated = unrelated
    this.reload()
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:TableSettings['activeFilters'], sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = []
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    if (pagination.current !== this.tableSettings.pagination.current) {
      window.scrollTo(0, 0)
    }

    // If items per page is changed, set current page to 1.
    if (pagination.pageSize !== this.tableSettings.pagination.pageSize) {
      this.tableSettings.pagination.current = 1
    }

    // Get list based on filters and search term.
    this.reload()
  }

  handleSearchChange () : void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
      this.cancelFetchingIssues()
    }

    this.timer = setTimeout(() => {
      const settings = { ...this.tableSettings }

      settings.pagination.current = 1
      settings.search = this.searchValue
      this.tableSettings = settings

      // Get list based on filters and search term.
      this.reload()
    }, this.timeout)
  }

  cancelFetchingIssues () : void {
    // cancel axios request
    axios.CancelToken.source().cancel()
  }

  getRowClassName (record: Record<string, unknown>): string {
    return 'read' in record ? record.read ? 'read' : 'unread' : ''
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          if (!this.selectedRowKeys.length) {
            const keyboard = window.event as KeyboardEvent
            if (can(this.settings.permissionNames.edit)) {
              this.handleRouting(this.settings.routeNames.edit, keyboard, rowData)
            } else if (can(this.settings.permissionNames.show)) {
              this.handleRouting(this.settings.routeNames.show, keyboard, rowData)
            }
          } else {
            this.selectedRowKeys.includes(rowData.id) ? this.selectedRowKeys.splice(this.selectedRowKeys.indexOf(rowData.id), 1) : this.selectedRowKeys.push(rowData.id)
          }
        }
      }
    }
  }

  private handleRouting (routeName:string, keyboard: KeyboardEvent, rowData: { id: number }) {
    if (keyboard.ctrlKey) {
      const route = this.$router.resolve({
        name: routeName,
        params: { [this.settings.routeParamKey]: rowData.id.toString() }
      })
      window.open(route.href, '_blank')
    } else {
      this.$router.push({
        name: routeName,
        params: { [this.settings.routeParamKey]: rowData.id.toString() }
      })
    }
  }

  onSelectChange (selectedRowKeys:number[]) : void {
    this.selectedRowKeys = selectedRowKeys
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  get items () : WrappedResponse<IssueModel> {
    return this.$store.getters['issueStore/list']
  }

  get filters () : Record<string, FilterOption[]> {
    return this.items.filters
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.items.meta.per_page),
      total: this.items.meta.total,
      showTotal: (total, range) => `${this.config.single} ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters['issueStore/loading'].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  columnFilterKeys: { [key: string]: string } = {
    'solver.id': 'solvers',
    source: 'sources',
    manager_id: 'managers',
    status: 'status',
    number: 'building_numbers',
    'solver_company.id': 'solver_companies',
    'project.id': 'projects',
    'project.entity.id': 'entities',
    type: 'types',
    priority: 'priorities'
  }

  @Watch('filters')
  onFiltersChange () : void {
    this.columns.forEach((column:Column) => {
      const filterKey = this.columnFilterKeys[column.key] || null
      if (filterKey && this.filters && filterKey in this.filters) {
        column.filters = this.filters[filterKey]
      }
    })
  }

  @Watch('rental')
  InitRental (): void {
    this.init()
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
